import { ref, createApp, h } from 'vue'
import ConfirmAction from '@/components/ConfirmAction.vue'
import axios from 'axios'

// Global state for the confirm modal
const confirmState = ref({
  isVisible: false,
  title: '',
  message: '',
  confirmText: 'Bevestigen',
  cancelText: 'Annuleren',
  type: 'primary',
  size: 'sm',
  showLoading: false,
  resolve: null,
  reject: null,
  url: null,
  method: 'GET',
  data: null,
  theme: 'default'
})

// Create a container element for the modal
let modalContainer = null
let modalApp = null

/**
 * Initialize the modal container if it doesn't exist
 */
function initializeModal() {
  if (modalContainer) return
  
  modalContainer = document.createElement('div')
  document.body.appendChild(modalContainer)
  
  // Create a simple wrapper component
  const ModalWrapper = {
    name: 'ConfirmActionWrapper',
    setup() {
      const state = confirmState
      
      const handleConfirm = async () => {
        const { url, method, data, resolve, reject } = state.value
        
        try {
          // If we have a URL, execute the action
          if (url) {
            state.value.showLoading = true
            
            let response
            const config = {
              method: method.toUpperCase(),
              url
            }
            
            // Add data for POST, PUT, PATCH requests
            if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
              config.data = data
            }
            
            response = await axios(config)
            
            state.value.showLoading = false
            
            // Close modal and resolve with response
            state.value.isVisible = false
            resolve(response.data)
          } else {
            // Simple confirmation without URL
            state.value.isVisible = false
            resolve(true)
          }
        } catch (error) {
          state.value.showLoading = false
          
          // For validation errors (422), don't close the modal
          if (error.response && error.response.status === 422) {
            // You could handle validation errors here if needed
            resolve(false)
          } else {
            // For other errors, close modal and reject
            state.value.isVisible = false
            reject(error)
          }
        }
      }
      
      const handleCancel = () => {
        state.value.isVisible = false
        if (state.value.resolve) {
          state.value.resolve(false)
        }
      }
      
      return () => h(ConfirmAction, {
        isVisible: state.value.isVisible,
        title: state.value.title,
        message: state.value.message,
        confirmText: state.value.confirmText,
        cancelText: state.value.cancelText,
        type: state.value.type,
        size: state.value.size,
        theme: state.value.theme,
        showLoading: state.value.showLoading,
        onConfirm: handleConfirm,
        onCancel: handleCancel
      })
    }
  }
  
  // Create and mount the app
  modalApp = createApp(ModalWrapper)
  modalApp.mount(modalContainer)
}

/**
 * Show a confirmation dialog
 * 
 * @param {string} message - The confirmation message
 * @param {Object} options - Additional options
 * @returns {Promise} - Resolves with true/false or response data
 */
export function useConfirmAction() {
  
  /**
   * Show a confirmation dialog
   * 
   * @param {string} message - The confirmation message
   * @param {Object} options - Additional options
   * @returns {Promise} - Resolves with true/false or response data
   */
  function confirmAction(message, options = {}) {
    // Initialize modal if not already done
    initializeModal()
    
    const {
      title = 'Bevestig Actie',
      confirmText = 'Bevestigen',
      cancelText = 'Annuleren',
      type = 'primary',
      size = 'sm',
      url = null,
      method = 'GET',
      data = null,
      theme = 'default'
    } = options
    
    return new Promise((resolve, reject) => {
      // Update state
      confirmState.value = {
        isVisible: true,
        title,
        message,
        confirmText,
        cancelText,
        type,
        size,
        theme,
        showLoading: false,
        resolve,
        reject,
        url,
        method,
        data
      }
    })
  }
  
  /**
   * Show a danger confirmation dialog
   * 
   * @param {string} message - The confirmation message
   * @param {Object} options - Additional options
   * @returns {Promise}
   */
  function confirmDanger(message, options = {}) {
    return confirmAction(message, { ...options, type: 'danger' })
  }
  
  /**
   * Show a warning confirmation dialog
   * 
   * @param {string} message - The confirmation message
   * @param {Object} options - Additional options
   * @returns {Promise}
   */
  function confirmWarning(message, options = {}) {
    return confirmAction(message, { ...options, type: 'warning' })
  }
  
  /**
   * Show an info confirmation dialog
   * 
   * @param {string} message - The confirmation message
   * @param {Object} options - Additional options
   * @returns {Promise}
   */
  function confirmInfo(message, options = {}) {
    return confirmAction(message, { ...options, type: 'info' })
  }
  
  /**
   * Execute an action with confirmation
   * 
   * @param {string} message - The confirmation message
   * @param {string} url - The URL to execute if confirmed
   * @param {Object} options - Additional options
   * @returns {Promise}
   */
  function confirmActionWithUrl(message, url, options = {}) {
    return confirmAction(message, { ...options, url })
  }
  
  return {
    confirmAction,
    confirmDanger,
    confirmWarning,
    confirmInfo,
    confirmActionWithUrl
  }
}